import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileSpreadsheet, ArrowLeft, Upload, Download, CheckCircle, XCircle, Image as ImgIcon, X, FileArchive } from "lucide-react";

export default function BulkGenerate() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const photosRef = useRef();
  const zipRef = useRef();
  const [tpl, setTpl] = useState(null);
  const [file, setFile] = useState(null);
  const [photos, setPhotos] = useState([]); // File[]
  const [photoZip, setPhotoZip] = useState(null); // File
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/templates/${templateId}`);
        setTpl(data);
      } catch (e) {
        console.error(e);
        toast.error("Template not found");
        navigate("/templates");
      }
    })();
  }, [templateId, navigate]);

  const downloadTemplate = () => {
    if (!tpl) return;
    const textKs = (tpl.fields || []).filter(f => f.type === "text").map(f => f.key);
    const photoKs = (tpl.fields || []).filter(f => f.type === "photo").map(f => f.key);
    if (textKs.length === 0 && photoKs.length === 0) { toast.error("This template has no fields"); return; }
    const allKeys = [...textKs, ...photoKs];
    const sample = allKeys.map(k => photoKs.includes(k) ? `${k}1.jpg` : "sample");
    const csv = allKeys.join(",") + "\n" + sample.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${tpl.name}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const addPhotos = (fileList) => {
    if (!fileList) return;
    const MAX_PER_FILE = 8 * 1024 * 1024;
    const arr = Array.from(fileList).filter(f => {
      if (!f.type.startsWith("image/")) return false;
      if (f.size > MAX_PER_FILE) {
        toast.error(`${f.name}: exceeds 8MB limit`);
        return false;
      }
      return true;
    });
    setPhotos(prev => {
      const seen = new Set(prev.map(p => p.name.toLowerCase()));
      const merged = [...prev];
      arr.forEach(f => { if (!seen.has(f.name.toLowerCase())) merged.push(f); });
      return merged;
    });
  };

  const removePhoto = (name) => {
    setPhotos(prev => prev.filter(p => p.name !== name));
  };

  const onZipPick = (f) => {
    if (!f) { setPhotoZip(null); return; }
    const isZip = f.name.toLowerCase().endsWith(".zip") || f.type === "application/zip" || f.type === "application/x-zip-compressed";
    if (!isZip) { toast.error("Please choose a .zip file"); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error("ZIP exceeds 200MB limit"); return; }
    setPhotoZip(f);
  };

  const submit = async () => {
    if (!file) { toast.error("Select an Excel file first"); return; }
    setBusy(true); setResults(null);
    try {
      const fd = new FormData();
      fd.append("template_id", templateId);
      fd.append("file", file);
      photos.forEach(p => fd.append("photos", p));
      if (photoZip) fd.append("photo_zip", photoZip);
      const { data } = await api.post("/idcards/bulk", fd);
      setResults(data);
      const ok = data.results.filter(r => !r.error).length;
      toast.success(`Generated ${ok}/${data.total} cards${(photos.length || photoZip) ? " with photos" : ""}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Bulk failed");
    } finally { setBusy(false); }
  };

  if (!tpl) return <div className="font-mono text-sm uppercase">Loading...</div>;

  const textKeys = (tpl.fields || []).filter(f => f.type === "text").map(f => f.key);
  const photoKeys = (tpl.fields || []).filter(f => f.type === "photo").map(f => f.key);
  const hasPhotoField = photoKeys.length > 0;

  return (
    <div className="space-y-6" data-testid="bulk-page">
      <div className="flex items-center gap-3">
        <Link to="/templates" className="p-2 brutal-border brutal-shadow-sm bg-white"><ArrowLeft className="w-4 h-4"/></Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#22C55E] font-bold mb-1">// Bulk generation</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Bulk: {tpl.name}</h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white brutal-border brutal-shadow p-6 space-y-6">
          {/* STEP 1: template download */}
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">// Step 1 · Download Excel template</div>
            <p className="text-sm mb-3">Excel columns must match these field keys:</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {textKeys.map(k => (<span key={k} className="font-mono text-xs bg-black text-white px-2 py-1">{k}</span>))}
              {photoKeys.map(k => (<span key={k} className="font-mono text-xs bg-[#22C55E] text-black px-2 py-1">{k} <span className="opacity-70">(filename)</span></span>))}
            </div>
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase text-xs tracking-widest px-4 py-3 brutal-border brutal-shadow-sm brutal-hover" data-testid="dl-template-btn">
              <Download className="w-4 h-4"/> Download CSV Template
            </button>
          </div>

          {/* STEP 2: Excel upload */}
          <div className="border-t-2 border-black pt-5">
            <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">// Step 2 · Upload filled Excel (.xlsx)</div>
            <button onClick={()=>fileRef.current?.click()} className="w-full flex flex-col items-center gap-2 py-6 border-2 border-dashed border-black bg-gray-50 hover:bg-gray-100" data-testid="pick-xlsx-btn">
              <Upload className="w-8 h-8 text-[#002FA7]"/>
              <div className="font-mono text-xs uppercase tracking-widest font-bold">{file ? file.name : "Choose .xlsx file"}</div>
            </button>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={e=>setFile(e.target.files?.[0])} className="hidden"/>
          </div>

          {/* STEP 3: Photos upload (only if template has photo field) */}
          {hasPhotoField && (
            <div className="border-t-2 border-black pt-5">
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">// Step 3 · Upload photos (optional)</div>
              <p className="text-xs text-gray-600 mb-3">Excel has {photoKeys.length} image column{photoKeys.length !== 1 ? "s" : ""} ({photoKeys.join(", ")}). Each row's cell in that column should be the exact filename of the corresponding photo/signature/stamp. Choose EITHER individual photos OR a ZIP — or use both.</p>

              {/* ZIP dropzone */}
              <div className="mb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">// Option A · ZIP of photos (recommended for 50+ people)</div>
                <button
                  onClick={()=>zipRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 py-5 border-2 border-dashed border-[#EAB308] bg-yellow-50 hover:bg-yellow-100"
                  data-testid="pick-zip-btn"
                >
                  <FileArchive className="w-7 h-7 text-[#EAB308]"/>
                  <div className="font-mono text-xs uppercase tracking-widest font-bold">
                    {photoZip ? `${photoZip.name} · ${(photoZip.size/1024/1024).toFixed(1)}MB` : "Choose ZIP file"}
                  </div>
                </button>
                <input ref={zipRef} type="file" accept=".zip,application/zip" onChange={e=>{ onZipPick(e.target.files?.[0]); e.target.value = ""; }} className="hidden"/>
                {photoZip && (
                  <button onClick={()=>setPhotoZip(null)} className="mt-2 inline-flex items-center gap-1 text-xs text-[#FF3B30] hover:underline font-mono uppercase tracking-widest font-bold" data-testid="remove-zip">
                    <X className="w-3 h-3"/> Remove ZIP
                  </button>
                )}
              </div>

              {/* Individual photos */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">// Option B · Individual photos</div>
                <button
                  onClick={()=>photosRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 py-5 border-2 border-dashed border-[#22C55E] bg-green-50 hover:bg-green-100"
                  data-testid="pick-photos-btn"
                >
                  <ImgIcon className="w-7 h-7 text-[#22C55E]"/>
                  <div className="font-mono text-xs uppercase tracking-widest font-bold">
                    {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? "s" : ""} selected · Add more` : "Choose multiple photos"}
                  </div>
                </button>
                <input
                  ref={photosRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={e=>{ addPhotos(e.target.files); e.target.value = ""; }}
                  className="hidden"
                />
                {photos.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1 bg-gray-50 border-2 border-black p-2" data-testid="photos-list">
                    {photos.map(p => (
                      <div key={p.name} className="flex items-center justify-between font-mono text-xs px-2 py-1 bg-white border border-gray-300">
                        <span className="truncate flex-1">{p.name}</span>
                        <span className="text-gray-500 mx-2">{(p.size/1024).toFixed(0)}kb</span>
                        <button onClick={()=>removePhoto(p.name)} className="text-[#FF3B30] hover:bg-red-50 p-1" data-testid={`remove-photo-${p.name}`}><X className="w-3 h-3"/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4: Submit */}
          <div className="border-t-2 border-black pt-5">
            <button onClick={submit} disabled={busy || !file} className="w-full inline-flex items-center justify-center gap-2 bg-[#22C55E] text-black font-bold uppercase tracking-widest text-sm px-5 py-4 brutal-border brutal-shadow brutal-hover disabled:opacity-60" data-testid="bulk-submit">
              <FileSpreadsheet className="w-4 h-4"/> {busy ? "Generating..." : "Generate All"}
            </button>
          </div>
        </div>

        <div className="bg-white brutal-border brutal-shadow p-6">
          <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-3">// Results</div>
          {!results ? (
            <div className="py-16 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-300"/>
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500">Awaiting bulk run</div>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-auto">
              {results.results.map((r, i) => (
                <div key={r.row} className={`p-3 border-2 border-black flex items-center justify-between ${r.error ? "bg-red-50" : "bg-green-50"}`} data-testid={`bulk-row-${i}`}>
                  <div className="font-mono text-xs">Row {r.row}</div>
                  {r.error ? (
                    <div className="flex items-center gap-1 text-[#FF3B30] font-mono text-xs"><XCircle className="w-4 h-4"/> {r.error}</div>
                  ) : (
                    <div className="flex items-center gap-1 text-[#22C55E] font-mono text-xs"><CheckCircle className="w-4 h-4"/> OK</div>
                  )}
                </div>
              ))}
              <Link to="/library" className="block text-center mt-3 bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-3 brutal-border brutal-shadow brutal-hover" data-testid="go-library">Go to Library →</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
